/**
 * gisUtils
 *
 * Pure utilities for parsing GeoJSON FeatureCollections from the GIS minify endpoint
 * into typed node and line arrays ready for DeckGL rendering.
 *
 * Field mappings (GeoJSON properties → domain types):
 *   NAME              → id  (falls back to "INLETNODE-OUTLETNODE")
 *   TYPE / collection → type  (lowercased; prefers FeatureCollection name)
 *   INLETNODE         → node1
 *   OUTLETNODE        → node2
 *   GEOM1             → sizePerc (min-max normalized 0–1, same logic as Python ConduitSimplification)
 *
 *   geometry.type === "LineString" → GisLine
 *   geometry.type !== "LineString" → GisNode
 */

// ── Types ───────────────────────────────────────────────────────────────────

export interface GisNode {
	id: string;
	longitude: number;
	latitude: number;
	type: string;
	maxDepth: number | null;
	initDepth: number;
	invertElev: number | null;
	pondedArea: number;
}

export interface GisLine {
	id: string;
	path: [number, number][];
	type: string;
	node1: string | null;
	node2: string | null;
	sizePerc: number;
	shape: string | null;
	length: number | null;
	maxQ: number | null;
	maxV: number | null;
	maxDPerc: number | null;
	maxQPerc: number | null;
}

export interface GisData {
	nodes: GisNode[];
	lines: GisLine[];
	dataCenter: { longitude: number; latitude: number } | null;
}

// ── Helpers ─────────────────────────────────────────────────────────────────

export function isValidCoord(lng: number, lat: number): boolean {
	return (
		typeof lng === 'number' &&
		typeof lat === 'number' &&
		isFinite(lng) &&
		isFinite(lat) &&
		lng >= -180 &&
		lng <= 180 &&
		lat >= -90 &&
		lat <= 90
	);
}

// ── Parser ───────────────────────────────────────────────────────────────────

/**
 * Parses a GeoJSON FeatureCollection's features array into typed GisNode and
 * GisLine arrays, and computes the bounding-box center for auto-zoom.
 */
export function parseGisFeatures(features: any[]): GisData {
	const nodes: GisNode[] = [];
	const lines: GisLine[] = [];

	// Pre-compute GEOM1 min/max for sizePerc normalization
	// Mirrors Python ConduitSimplification: size_perc = (size - min_size) / (max_size - min_size)
	// Fallback 0.001 matches the Python script behaviour for missing/NaN GEOM1
	let minGeom1 = Infinity;
	let maxGeom1 = -Infinity;
	for (const feature of features) {
		if (feature?.geometry?.type !== 'LineString') continue;
		const raw = feature?.properties?.GEOM1;
		const val = typeof raw === 'number' && isFinite(raw) ? raw : 0.001;
		if (val < minGeom1) minGeom1 = val;
		if (val > maxGeom1) maxGeom1 = val;
	}
	const geom1Range = isFinite(minGeom1) ? maxGeom1 - minGeom1 : 0;

	let minLng = Infinity,
		maxLng = -Infinity;
	let minLat = Infinity,
		maxLat = -Infinity;

	const trackCoord = (lng: number, lat: number) => {
		if (lng < minLng) minLng = lng;
		if (lng > maxLng) maxLng = lng;
		if (lat < minLat) minLat = lat;
		if (lat > maxLat) maxLat = lat;
	};

	for (const feature of features) {
		const props = feature?.properties ?? {};
		const geom = feature?.geometry;
		if (!geom) continue;

		const id: string = props.NAME ?? `${props.INLETNODE ?? ''}-${props.OUTLETNODE ?? ''}`;
		const rawType = props.TYPE;

		if (geom.type === 'LineString') {
			const type: string = rawType ? (rawType as string).toLowerCase() : 'link';
			const path: [number, number][] = (geom.coordinates ?? [])
				.filter(([lng, lat]: number[]) => isValidCoord(lng, lat))
				.map(([lng, lat]: number[]) => [lng, lat] as [number, number]);

			if (path.length < 2) continue;

			path.forEach(([lng, lat]) => trackCoord(lng, lat));

			lines.push({
				id,
				path,
				type,
				node1: props.INLETNODE ?? null,
				node2: props.OUTLETNODE ?? null,
				sizePerc: (() => {
				const raw = props.GEOM1;
				const geom1 = typeof raw === 'number' && isFinite(raw) ? raw : 0.001;
				return geom1Range > 0 ? (geom1 - minGeom1) / geom1Range : 0;
			})(),
				shape: props.SHAPE ?? null,
				length: typeof props.LENGTH === 'number' ? props.LENGTH : null,
				maxQ: typeof props.MAXQ === 'number' ? props.MAXQ : null,
				maxV: typeof props.MAXV === 'number' ? props.MAXV : null,
				maxDPerc: typeof props.MAXDPERC === 'number' ? props.MAXDPERC : null,
				maxQPerc: typeof props.MAXQPERC === 'number' ? props.MAXQPERC : null,
			});
		} else {
			const type: string = rawType ? (rawType as string).toLowerCase() : 'node';
			const coords: number[] = geom.coordinates ?? [0, 0];
			const [lng, lat] = coords;
			if (!isValidCoord(lng, lat)) continue;
			trackCoord(lng, lat);
			nodes.push({
				id,
				longitude: lng,
				latitude: lat,
				type,
				maxDepth: typeof props.MAXDEPTH === 'number' ? props.MAXDEPTH : null,
				initDepth: typeof props.INITDEPTH === 'number' ? props.INITDEPTH : 0,
				invertElev: typeof props.INVERTELEV === 'number' ? props.INVERTELEV : null,
				pondedArea: typeof props.PONDEDAREA === 'number' ? props.PONDEDAREA : 0,
			});
		}
	}

	const dataCenter =
		isFinite(minLng)
			? {
					longitude: (minLng + maxLng) / 2,
					latitude: (minLat + maxLat) / 2,
			  }
			: null;

	return { nodes, lines, dataCenter };
}

export const EMPTY_GIS_DATA: GisData = { nodes: [], lines: [], dataCenter: null };

/**
 * Accepts the raw backend response, which is either a single FeatureCollection
 * or an array of FeatureCollections (as returned by /links/locations/{id}/minify),
 * flattens all features, and delegates to parseGisFeatures.
 */
export function parseGisResponse(response: any): GisData {
	if (!response) return EMPTY_GIS_DATA;
	const collections: any[] = Array.isArray(response) ? response : [response];
	const allFeatures = collections.flatMap((fc: any) => fc?.features ?? []);
	return parseGisFeatures(allFeatures);
}
